import { XMarkIcon } from '@heroicons/react/24/outline'
import { WarningHexagon } from 'iconoir-react'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { UpgradeModal } from 'app/components/UpgradeModal'
import { useUser } from 'app/utils'
import { useAuthStore } from 'app/stores/StoreContext'

export type ContactDrawerBannerProps = {
  isExpanded?: boolean
  onUpgrade?: () => void
  onDismiss?: () => void
}

export function ContactDrawerBannerContent({ isExpanded = true, onUpgrade, onDismiss }: ContactDrawerBannerProps) {
  return (
    <div
      data-expanded={isExpanded || undefined}
      className="group mx-auto flex max-w-[415px] items-center justify-between rounded-md bg-surface-muted px-4 py-3 data-[expanded]:max-w-[calc(100%-8px)] w-full data-[expanded]:py-1.5"
    >
      <div className="flex items-start group-data-[expanded]:items-center">
        <div className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-[5px] bg-inverse">
          <WarningHexagon className="h-3 w-3 text-inverse" />
        </div>
        <span className="ml-4 text-xs font-medium leading-[1.25] text-strong">
          You're viewing a contact preview. Upgrade to Trackable CRM to unlock full contact features.
        </span>
      </div>
      <div className="flex items-center gap-2">
        <button
          onClick={onUpgrade}
          className="h-8 whitespace-nowrap rounded-md border border-none pl-4 text-sm font-medium text-blue-600 underline shadow-elevation-1 hover:text-blue-700"
        >
          Upgrade to CRM
        </button>
        <button onClick={onDismiss} className="flex-shrink-0 text-minimal hover:text-subtle">
          <XMarkIcon className="h-5 w-5 text-strong" />
        </button>
      </div>
    </div>
  )
}

// observer: reads authStore.products in render. products is reassigned in place on a
// workspace switch, so without observer the product-gated banner would not react (TRK-6020).
export const ContactDrawerBanner = observer(function ContactDrawerBanner({
  isExpanded = true,
}: {
  isExpanded?: boolean
}) {
  const { products } = useAuthStore()
  const user = useUser()
  const [dismissed, setDismissed] = useState(false)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)

  if (!user) return null

  const hasAgent = products?.includes(`AI Agent`)
  const hasCRM = products?.includes(`CRM`)

  if (hasAgent && !hasCRM && !dismissed && user.workspaceId != null) {
    return (
      <>
        <ContactDrawerBannerContent
          isExpanded={isExpanded}
          onUpgrade={() => setShowUpgradeModal(true)}
          onDismiss={() => setDismissed(true)}
        />
        <UpgradeModal open={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} />
      </>
    )
  }

  return null
})
